import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Address from "_shared/addresses/address";
import AddressSchema from "_shared/addresses/address-schema";
import Guid from "_shared/guids/guid";
import EntityNameSchema from "legal-entities/entity-names/_schema/entity-name-schema";
import EntityName from "legal-entities/entity-names/entity-name";
import { z } from "zod";

export type EntityAgreementJSON = z.infer<typeof EntityAgreementSchema.EntityAgreementJSONZ>;
export type EntityAgreementParams = z.infer<typeof EntityAgreementSchema.EntityAgreementZ>;

export default class EntityAgreementSchema {
  public static readonly EntityAgreementJSONZ = z.object({
    id: z.uuid(),
    entityName: EntityNameSchema.EntityNameJSONZ,
    entityLocation: AddressSchema.AddressJSONZ.nullish(),
    termsFileId: z.uuid().nullish(),
  });

  public static readonly EntityAgreementZ = z.object({
    id: z.instanceof(Guid),
    entityName: z.instanceof(EntityName),
    entityLocation: z.instanceof(Address).optional(),
    termsFileId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown, logError: boolean = true): EntityAgreementParams {
    return parseOrThrow(EntityAgreementSchema.EntityAgreementZ, values, {
      logError: logError,
      schemaName: "EntityAgreementSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): EntityAgreementJSON {
    return parseOrThrow(EntityAgreementSchema.EntityAgreementJSONZ, json, {
      logError: true,
      schemaName: "EntityAgreementSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is EntityAgreementParams {
    try {
      EntityAgreementSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is EntityAgreementJSON {
    try {
      EntityAgreementSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: EntityAgreementJSON): EntityAgreementParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(EntityAgreementSchema.EntityAgreementJSONZ, json, {
      logError: true,
      schemaName: "EntityAgreementSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return EntityAgreementSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: EntityAgreementParams): EntityAgreementJSON {
    const parsed = EntityAgreementSchema.validate(legalEntity);
    return EntityAgreementSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    EntityAgreementSchema.EntityAgreementJSONZ,
    EntityAgreementSchema.EntityAgreementZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id)!,
        entityName: new EntityName(EntityNameSchema.deserialize(data.entityName)),
        entityLocation: Address.fromJSON(data.entityLocation) ?? undefined,
        termsFileId: Guid.fromJSON(data.termsFileId) ?? undefined,
      }),
      encode: (agreement) => ({
        id: agreement.id.toJSON(),
        entityName: agreement.entityName.toJSON(),
        entityLocation: agreement.entityLocation?.toJSON(),
        termsFileId: agreement.termsFileId?.toJSON(),
      }),
    },
  );
}
